import { TimeRange } from "@dtm-frontend/shared/ui";
import { ArrayElementType, ArrayUtils, StringUtils } from "@dtm-frontend/shared/utils";
import { Point, Polygon } from "@turf/helpers";
import {
    AirspaceElement,
    AirspaceElementResponseBody,
    AirspaceElementStatus,
    AirspaceElementsInfo,
    AirspaceScope,
    DronePoint,
    DronePointDetails,
    GeoZoneType,
    ScopeResponseElement,
} from "../models/geo-zones.models";

const TOOLTIP_REGEX = /(.+?)<tooltip>(.+?)<\/tooltip>/;

export type DronePointsResponseBody = Array<{
    id: string;
    name: string;
    center: Point;
    area: Polygon;
}>;

export interface DronePointDetailsResponseBody {
    id: string;
    name: string;
    address: string;
    description: string;
    center: Point;
    area: Polygon;
    photoIds: string[];
}

function parseTextWithTooltip(string: string): { text: string; tooltip?: string } {
    const matches = TOOLTIP_REGEX.exec(string);

    if (!matches) {
        return { text: string };
    }

    return {
        text: matches[1],
        tooltip: matches[2],
    };
}

// NOTE: JS date parser requires 4 or 6 digit year (in ISO format), but BE can return 5 digit year
// ISO 8601 allows more than 4 digits for year, but it's not standardized so there can be differences between parsers
function parseBigDate(dateString: string) {
    const testDate = new Date(dateString);
    if (!isNaN(testDate.getTime())) {
        return testDate;
    }
    const fixedString = dateString.replace(/\+(\d{5})(?=-)/, "+0$1");

    return new Date(fixedString);
}

function convertScope(scope: ScopeResponseElement): AirspaceScope {
    return {
        startTime: parseBigDate(scope.startTime),
        endTime: parseBigDate(scope.endTime),
        lowerLimit: Math.ceil(scope.lowerLimit), // NOTE: Backend rounds up in analysis messages
        upperLimit: Math.ceil(scope.upperLimit),
    };
}

const DTM_TAG_NAME = "DTM";
const ALERT_TAG_NAME = "UTM_ALERT";

function checkIsDtm(element: ArrayElementType<AirspaceElementResponseBody["airspaceElements"]>) {
    return element.tag?.includes(DTM_TAG_NAME);
}

export function convertAirspaceElementResponseBodyToAirspaceElement(
    response: AirspaceElementResponseBody,
    endTime?: Date | null,
    scopeTimeRange?: TimeRange
): AirspaceElementsInfo {
    const aupEndTime = response.aupEndTime ? new Date(response.aupEndTime) : undefined;
    const airacEndTime = response.airacEndTime ? new Date(response.airacEndTime) : undefined;
    const serverTime = response.serverTime ? new Date(response.serverTime) : undefined;
    const doesAupExist = !!(aupEndTime && endTime && aupEndTime.getTime() >= endTime.getTime());

    return {
        airspaceElements: response.airspaceElements
            .filter(({ geozone }) => geozone)
            .map((element) => ({
                activeTime: {
                    from: new Date(element.scope.startTime),
                    to: new Date(element.scope.endTime),
                },
                geometry: element.geometry,
                id: element.airspaceElementId ?? element.airspaceElementIds[0],
                ids: element.airspaceElementIds,
                isActive: element.status === AirspaceElementStatus.Active,
                isStatic: element.staticElement,
                isAlert: element.tag === ALERT_TAG_NAME,
                geoZoneType: checkIsDtm(element) ? GeoZoneType.DroneTrafficManagement : element.geozone,
                type: element.type,
                data: element, // NOTE: Debugging purpose
                designator: element.designator,
                reservations: element.reservations
                    .map((reservation) => ({
                        id: reservation.id,
                        reservedBy: reservation.reservedBy,
                        scope: convertScope(reservation.scope),
                        status: reservation.status,
                    }))
                    .sort((left, right) => left.scope.startTime.getTime() - right.scope.startTime.getTime()),
                scope: convertScope(element.scope),
                information: element.information?.reduce<AirspaceElement["information"]>(
                    (information, currentInformation) => ({
                        ...information,
                        [currentInformation.languageTag]: {
                            flightConditions:
                                currentInformation.flightConditions && currentInformation.flightConditions.length > 1
                                    ? [parseTextWithTooltip(currentInformation.flightConditions)]
                                    : undefined,
                            description: parseTextWithTooltip(currentInformation.description),
                            contact: currentInformation.contact,
                        },
                    }),
                    {}
                ),
                isH24: !!element.h24,
                parent: element.parent,
                supervisor: element.supervisor,
                isOrdered: element.reservations.some((reservation) => reservation.status === AirspaceElementStatus.Active),
                zoneAttributes: element.zoneAttributes
                    ? {
                          isAdsbForBvlosRequired: element.zoneAttributes.adsbForBvlosRequired,
                          isBvlosFlightsForbidden: element.zoneAttributes.bvlosFlightsForbidden,
                          isBvlosPathFlightsForbidden: element.zoneAttributes.bvlosPathFlightsForbidden,
                          isVlosFlightsForbidden: element.zoneAttributes.vlosFlightsForbidden,
                          localInformationType: element.zoneAttributes.localInformationType,
                          name: element.zoneAttributes.name,
                      }
                    : undefined,
            })),
        doesAupExist,
        aupEndTime,
        scopeTimeRange: scopeTimeRange,
        airacEndTime,
        serverTime,
    };
}

export function convertDronePointsResponseBodyToDronePoints(response: DronePointsResponseBody): DronePoint[] {
    return response.map((dronePointResponse) => ({ ...dronePointResponse }));
}

export function convertDronePointDetailsResponseBodyToDronePointDetails(
    response: DronePointDetailsResponseBody,
    photoUrlTemplate: string
): DronePointDetails {
    const uniqueBorderPoints = ArrayUtils.unique(response.area.coordinates.flat(), (item) => `${item[0]},${item[1]}`);

    return {
        id: response.id,
        name: response.name,
        address: response.address,
        description: response.description,
        photoUrls: response.photoIds.map((photoId) =>
            StringUtils.replaceInTemplate(photoUrlTemplate, { dronePointId: response.id, id: photoId })
        ),
        centerPoint: {
            latitude: response.center.coordinates[0],
            longitude: response.center.coordinates[1],
        },
        borderPoints: uniqueBorderPoints.map((coordinate) => ({
            latitude: coordinate[0],
            longitude: coordinate[1],
        })),
    };
}
